<template>
  <fieldset :id="'step' + formStore.currentStep + 'Fieldset'" class="u-spaceItems3">
    <CardContent>
      <legend>
        <h2 class="u-textItalic u-textGrow2 Groove u-padBottom04">
          <span>Provider Information</span>
        </h2>
      </legend>
      <div>
        <ul class="u-listReset u-spaceItems3">
          <li
            v-for="(provider, index) in providers"
            :key="`${provider.providerId}-${renderKey}`"
            class="provider-item"
          >
            <Collapse
              :id="'providerCollapse' + index"
              :initiallyExpanded="shouldExpandProvider(provider, index)"
            >
              <template #label>
                <div class="u-flexBetween u-flexAlignCenter">
                  <span class="u-textBlackAlt">
                    <span v-if="provider.firstName !== '' && provider.lastName !== ''">
                      {{ provider.firstName }} {{ provider.lastName }}
                    </span>
                    <span v-else>Provider {{ index + 1 }} Details</span>
                  </span>
                </div>
              </template>
              <template #content>
                <!-- Provider Name & Information -->
                <fieldset class="u-spaceItems1">
                  <legend>
                    <h4>Provider Name & Information</h4>
                  </legend>
                  <div class="Grid Grid--withGap">
                    <div class="Grid-cell u-sm-size3of5 u-md-size2of6">
                      <BaseInput
                        v-model="provider.firstName"
                        :id="'providerFirstName' + index"
                        :name="'providerFirstName' + index"
                        label="First Name"
                        type="text"
                        required
                        :maxlength="50"
                        class="is-required"
                      />
                      <!-- Hidden input for provider ID -->
                      <input
                        type="hidden"
                        :id="'providerId' + index"
                        :name="'providerId' + index"
                        :value="provider.providerId"
                      />
                    </div>
                    <div class="Grid-cell u-sm-size2of5 u-md-size1of6">
                      <BaseInput
                        v-model="provider.middleInitial"
                        :id="'providerMiddleInitial' + index"
                        :name="'providerMiddleInitial' + index"
                        label="Middle Initial"
                        type="text"
                        :maxlength="1"
                      />
                    </div>
                    <div class="Grid-cell u-sm-size3of5 u-md-size2of6">
                      <BaseInput
                        v-model="provider.lastName"
                        :id="'providerLastName' + index"
                        :name="'providerLastName' + index"
                        label="Last Name"
                        type="text"
                        required
                        :maxlength="50"
                        class="is-required"
                      />
                    </div>
                    <div class="Grid-cell u-sm-size2of5 u-md-size1of6">
                      <BaseInput
                        v-model="provider.suffix"
                        :id="'providerSuffix' + index"
                        :name="'providerSuffix' + index"
                        label="Suffix"
                        type="text"
                        :maxlength="5"
                      />
                    </div>
                    <!-- Date of Birth -->
                    <div class="Grid-cell u-md-size1of2">
                      <DateOfBirthGroup
                        v-model="provider.dateOfBirth"
                        :index="index"
                        :id="'providerDob' + index"
                        required
                      />
                    </div>
                    <!-- Preferred or Alternate Name -->
                    <div class="Grid-cell u-md-size1of2">
                      <BaseInput
                        v-model="provider.alias"
                        :id="'providerAlias' + index"
                        :name="'providerAlias' + index"
                        label="Preferred or Alternate Name"
                        type="text"
                        :maxlength="100"
                      />
                    </div>
                    <!-- NPI Number -->
                    <div class="Grid-cell">
                      <NpiField
                        v-model="provider.npi"
                        :id="'providerNpi' + index"
                        :name="'providerNpi' + index"
                        label="NPI Number"
                        required
                      />
                    </div>
                    <!-- Degree select field -->
                    <div class="Grid-cell">
                      <BaseSelect
                        v-model="provider.degree"
                        :id="'providerDegree' + index"
                        :name="'providerDegree' + index"
                        label="Degree"
                        :options="degrees"
                        option-value="DegreeID"
                        option-label="DegreeName"
                        required
                      />
                    </div>
                    <!-- Phone number -->
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        v-model="provider.phone"
                        :id="'providerPhone' + index"
                        :name="'providerPhone' + index"
                        label="Phone Number"
                        type="tel"
                        mask="###-###-####"
                        helpText="Format: 123-456-7890"
                        :maxlength="12"
                        required
                      />
                    </div>
                    <!-- Email address -->
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        v-model="provider.email"
                        :id="'providerEmail' + index"
                        :name="'providerEmail' + index"
                        label="Email Address"
                        type="email"
                        :maxlength="100"
                        required
                      />
                    </div>
                    <!-- CMS options -->
                    <div class="Grid-cell">
                      <RadioButtonGroup
                        v-model="provider.cms"
                        :name="'providerCms' + index"
                        :id="'providerCms' + index"
                        :options="[
                          {
                            id: 'providerCmsYes' + index,
                            label: 'Yes',
                            value: true,
                            selected: true,
                          },
                          { id: 'providerCmsNo' + index, label: 'No', value: false },
                        ]"
                        group-label="CMS (Medicare/Medicaid) Participation"
                        required
                      />
                    </div>
                  </div>
                </fieldset>
                <hr />
                <!-- Provider Specialty Information -->
                <fieldset :id="'providerSpecialtyInfo' + index" class="u-spaceItems1">
                  <legend>
                    <h4>Provider Specialty Information</h4>
                  </legend>
                  <div class="Grid Grid--withGap">
                    <!-- Primary specialty -->
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseSelect
                        v-model="provider.primarySpecialty"
                        :id="'providerPrimarySpecialty' + index"
                        :name="'providerPrimarySpecialty' + index"
                        label="Primary Specialty"
                        :options="groupedSpecialties"
                        option-value="value"
                        option-label="label"
                        required
                      />
                    </div>
                    <!-- Secondary specialty -->
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseSelect
                        v-model="provider.secondarySpecialty"
                        :id="'providerSecondarySpecialty' + index"
                        :name="'providerSecondarySpecialty' + index"
                        label="Secondary Specialty"
                        :options="groupedSpecialties"
                        option-value="value"
                        option-label="label"
                      />
                    </div>
                  </div>
                </fieldset>
                <!-- Provider Practice & Location Information -->
                <fieldset :id="'providerPracticeInfo' + index" class="u-spaceItems1">
                  <legend>
                    <h4>Provider Practice & Location Information</h4>
                  </legend>
                  <div class="Grid Grid--withGap">
                    <!-- Practice -->
                    <div class="Grid-cell">
                      <BaseSelect
                        :options="
                          formStore.membershipApplicationInfo.practices
                            .filter(
                              (practice) =>
                                practice.practiceName && practice.practiceName.trim() !== '',
                            )
                            .map((practice) => {
                              console.log('Practice option:', practice.practiceName, 'practiceId:', practice.practiceId, 'type:', typeof practice.practiceId)
                              return {
                                value: practice.practiceId,
                                label: practice.practiceName,
                              }
                            })
                        "
                        :id="'providerPractice' + index"
                        :name="'providerPractice' + index"
                        label="Assign a Practice"
                        placeholder="Select a practice"
                        required
                        v-model="provider.providerLocations[0].practiceId"
                      />
                    </div>
                    <!-- Primary Location -->
                    <div class="Grid-cell">
                      <BaseSelect
                        :options="getLocationsForProvider(index)"
                        :id="'providerLocation' + index"
                        :name="'providerLocation' + index"
                        label="Assign a Location"
                        optionLabel="locationName"
                        optionValue="locationId"
                        required
                        :disabled="!provider.providerLocations[0].practiceId"
                        :placeholder="
                          !provider.providerLocations[0].practiceId
                            ? 'Please select a practice first'
                            : getLocationsForProvider(index).length === 0
                              ? formStore.applicationType === 'add'
                                ? 'No locations available for this practice from TIN search'
                                : 'No locations available for this practice'
                              : 'Select a location'
                        "
                        :modelValue="getPrimaryLocationId(index)"
                        @update:modelValue="setPrimaryLocation(index, $event)"
                      />
                    </div>
                    <!-- Additional Locations -->
                    <div
                      v-if="
                        getPrimaryLocationId(index) &&
                        getAvailableAdditionalLocations(index).length > 0
                      "
                      class="Grid-cell"
                    >
                      <label class="u-block u-textBlackAlt u-spaceBottom04"
                        >Additional Locations</label
                      >
                      <p
                        :id="'additionalLocationsInstructions' + index"
                        class="u-textShrink1 u-spaceBottom1"
                      >
                        Select any additional locations for this provider
                      </p>
                      <div
                        class="u-spaceItems03"
                        :aria-labelledby="'additionalLocationsInstructions' + index"
                      >
                        <div
                          v-for="location in getAvailableAdditionalLocations(index)"
                          :key="location.value"
                          class="checkbox-container"
                        >
                          <label
                            :for="'additionalLocation' + index + '_' + location.value"
                            class="Toggle Toggle--checkbox u-textBlackAlt"
                          >
                            <input
                              :id="'additionalLocation' + index + '_' + location.value"
                              :name="'additionalLocations' + index"
                              type="checkbox"
                              class="Toggle-input"
                              :checked="isAdditionalLocationSelected(index, location.value)"
                              @change="toggleAdditionalLocation(index, location.value)"
                            />
                            <span class="Toggle-indicator"></span>
                            {{ location.label }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <!-- Staff Priviledges -->
                    <div class="Grid-cell">
                      <RadioButtonGroup
                        v-model="provider.isPrivleged"
                        :name="'providerPrivileges' + index"
                        :id="'providerPrivileges' + index"
                        :options="[
                          {
                            id: 'providerPrivilegesYes' + index,
                            label: 'Yes',
                            value: true,
                            selected: true,
                          },
                          { id: 'providerPrivilegesNo' + index, label: 'No', value: false },
                        ]"
                        group-label="Medical staff privileges at a Baptist Health facility?"
                        required
                      />
                    </div>
                  </div>
                </fieldset>
              </template>
              <template #removeButton>
                <div class="u-spaceLeft03 u-spaceTop03">
                  <button
                    v-if="providers.length > 1"
                    @click.stop="removeProviderByIndex(index)"
                    type="button"
                    class="Button Button--compact Button--link u-textRed u-textShrink1 u-flexExpandLeft"
                    title="Remove this provider"
                  >
                    <span>
                      <svg
                        class="Icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M18.01,20.132,3.868,5.99A1.5,1.5,0,0,1,5.99,3.868L20.132,18.01a1.5,1.5,0,1,1-2.121,2.121Z"
                        />
                        <path
                          d="M3.868,20.132a1.5,1.5,0,0,1,0-2.121L18.01,3.868A1.5,1.5,0,1,1,20.132,5.99L5.99,20.132A1.5,1.5,0,0,1,3.868,20.132Z"
                        />
                      </svg>
                    </span>
                    <span> Remove<span class="u-hiddenVisually"> Provider</span> </span>
                  </button>
                </div>
              </template>
            </Collapse>
          </li>
        </ul>
      </div>
      <div class="u-spaceTop2">
        <button @click="addProvider" type="button" class="Button Button--compact Button--tertiary">
          <span>
            <svg
              class="Icon u-textColorInherit u-textShrink2"
              role="presentation"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                d="M22,13.5H2a1.5,1.5,0,0,1,0-3H22a1.5,1.5,0,0,1,0,3Z"
                class="Icon-rotateCounter1of8"
              />
              <path
                d="M12,23.5A1.50007,1.50007,0,0,1,10.5,22V2a1.5,1.5,0,0,1,3,0V22A1.50039,1.50039,0,0,1,12,23.5Z"
                class="Icon-rotateCounter1of8"
              />
            </svg>
          </span>
          Add Provider
        </button>
      </div>
    </CardContent>
  </fieldset>
</template>

<script setup>
import { computed, watch, onMounted, ref, nextTick } from 'vue'
import { useFormStore } from '../../stores/formStore'
import degrees from '@/data/degrees.json'
import specialties from '@/data/specialties.json'
import Collapse from '../Collapse.vue'
import BaseInput from '../fields/BaseInput.vue'
import BaseSelect from '../fields/BaseSelect.vue'
import RadioButtonGroup from '../fields/RadioButtonGroup.vue'
import CardContent from '../CardContent.vue'
import DateOfBirthGroup from '../fields/DateOfBirthGroup.vue'
import NpiField from '../fields/NpiField.vue'

const formStore = useFormStore()

// Define emit for validity changes
const emit = defineEmits(['validity-changed'])

// Local reactive refs for simpler management
const providers = ref([])
const allLocations = ref([])

// Initialize data from store
const initializeProviders = () => {
  providers.value = [...formStore.membershipApplicationInfo.providers]
  console.log('Initialized providers:', providers.value)
}

const initializeLocations = () => {
  // Build new locations array without clearing the existing one first
  const newLocations = []

  // Collect all locations from all practices
  formStore.membershipApplicationInfo.practices.forEach((practice) => {
    console.log('Processing practice:', practice.practiceName, 'with locations:', practice.locations)

    if (practice.locations && practice.locations.length > 0) {
      practice.locations.forEach((location) => {
        console.log('Processing location:', location)

        // For add applications with TIN search data, be more lenient with validation
        // since TIN search might not populate all fields initially
        const isValidLocation = location.locationId &&
          (location.locationName && location.locationName.trim() !== '') ||
          (formStore.applicationType === 'add' && location.isFromTinSearch)

        if (isValidLocation) {
          newLocations.push({
            ...location,
            practiceId: practice.practiceId,
            practiceName: practice.practiceName,
          })
          console.log('Added location to allLocations:', location.locationName || `Location ${location.locationId}`)
        } else {
          console.log('Skipped invalid location:', location)
        }
      })
    } else {
      console.log('Practice has no locations:', practice.practiceName)
    }
  })

  // Only update allLocations after the new array is complete
  allLocations.value = newLocations
  console.log('Initialized locations for Step 5, total count:', allLocations.value.length, allLocations.value)
}

// Sync providers back to store
const syncProvidersToStore = () => {
  formStore.membershipApplicationInfo.providers = [...providers.value]
}

// Watch for changes and sync to store
watch(
  providers,
  () => {
    syncProvidersToStore()
  },
  { deep: true },
)

// Group specialties by main specialty for better organization
const groupedSpecialties = computed(() => {
  const groups = {}

  specialties.forEach((specialty) => {
    // Extract the main specialty name (part before the first dash, if any)
    const specialtyName = specialty.Specialty
    let mainSpecialty

    if (specialtyName.includes(' - ')) {
      // This is a subspecialty, extract the main specialty
      mainSpecialty = specialtyName.split(' - ')[0]
    } else {
      // This is a main specialty
      mainSpecialty = specialtyName
    }

    // Initialize the group if it doesn't exist
    if (!groups[mainSpecialty]) {
      groups[mainSpecialty] = {
        label: mainSpecialty,
        options: [],
      }
    }

    // Add the specialty to the appropriate group
    groups[mainSpecialty].options.push({
      value: specialty.SpecialtyID,
      label: specialty.Specialty,
      isMainSpecialty: !specialtyName.includes(' - '),
    })
  })

  // Convert to array and sort
  return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label))
})

// Function to get filtered locations for a specific provider based on their selected practice
const getLocationsForProvider = (providerIndex) => {
  const provider = providers.value[providerIndex]
  const selectedPracticeId = provider?.providerLocations?.[0]?.practiceId

  if (!selectedPracticeId) {
    return []
  }

  console.log('Getting locations for provider', providerIndex, 'with practiceId:', selectedPracticeId)
  console.log('Available allLocations:', allLocations.value)

  // Filter locations that belong to the selected practice
  // Use loose equality to handle string/number type mismatches
  const availableLocations = allLocations.value
    .filter((location) => {
      console.log('Comparing location practiceId', location.practiceId, 'with selected', selectedPracticeId)
      return location.practiceId == selectedPracticeId
    })
    .map((location) => ({
      locationId: location.locationId,
      locationName: location.locationName || `Location ${location.locationId}`,
    }))

  console.log('Filtered locations for practice:', availableLocations)

  // During initialization, if we have a selected location that's not in the available list,
  // include it temporarily to prevent the dropdown from losing its value
  const currentSelectedLocationId = provider?.providerLocations?.find(
    (loc) => loc.isPrimary,
  )?.locationId
  if (
    currentSelectedLocationId &&
    !availableLocations.find((loc) => loc.locationId == currentSelectedLocationId)
  ) {
    // Find the location in the original data to get its name
    const selectedLocation = allLocations.value.find(
      (loc) => loc.locationId == currentSelectedLocationId,
    )
    if (selectedLocation) {
      availableLocations.unshift({
        locationId: selectedLocation.locationId,
        locationName: selectedLocation.locationName || `Location ${selectedLocation.locationId}`,
      })
    }
  }

  return availableLocations
}

// Function to get the primary location ID for a provider
const getPrimaryLocationId = (providerIndex) => {
  const provider = providers.value[providerIndex]
  const primaryLocation = provider?.providerLocations?.find((loc) => loc.isPrimary === true)
  return primaryLocation?.locationId || ''
}

// Function to set a location as primary for a provider
const setPrimaryLocation = (providerIndex, locationId) => {
  const provider = providers.value[providerIndex]
  if (!provider.providerLocations || !locationId) return

  const selectedPracticeId = provider.providerLocations[0]?.practiceId
  if (!selectedPracticeId) return

  // Get the location details from local locations
  const locationDetails = allLocations.value.find((loc) => loc.locationId == locationId)
  if (!locationDetails) return

  // Clear all existing locations and rebuild with the new primary
  provider.providerLocations = [
    {
      practiceId: selectedPracticeId,
      locationId: locationId,
      // LocationName: locationDetails.locationName || '',
      isPrimary: true,
    },
  ]

  console.log(`Set location ${locationId} as primary for provider ${providerIndex}`)
}

// Function to get available additional locations (excluding primary location)
const getAvailableAdditionalLocations = (providerIndex) => {
  const provider = providers.value[providerIndex]
  const primaryLocationId = getPrimaryLocationId(providerIndex)
  const selectedPracticeId = provider?.providerLocations?.[0]?.practiceId

  if (!selectedPracticeId || !primaryLocationId) {
    return []
  }

  // Filter locations that belong to the selected practice, excluding the primary location
  // Use loose equality to handle string/number type mismatches
  const availableLocations = allLocations.value
    .filter(
      (location) =>
        location.practiceId == selectedPracticeId && location.locationId != primaryLocationId,
    )
    .filter((location) => location.locationId && location.locationName && location.locationName.trim() !== '')

  // Don't show additional locations section if there's only one location for this practice
  const totalLocationsForPractice = allLocations.value.filter(
    (location) => location.practiceId == selectedPracticeId
  ).length

  if (totalLocationsForPractice <= 1) {
    return []
  }

  return availableLocations
    .map((location) => ({
      value: location.locationId,
      label: location.locationName || `Location ${location.locationId}`,
    }))
}

// Function to check if an additional location is selected for a provider
const isAdditionalLocationSelected = (providerIndex, locationId) => {
  const provider = providers.value[providerIndex]
  return provider.providerLocations.some(
    (loc) => loc.locationId == locationId && loc.isPrimary === false,
  )
}

// Function to toggle additional location selection
const toggleAdditionalLocation = (providerIndex, locationId) => {
  const provider = providers.value[providerIndex]
  if (!provider.providerLocations || provider.providerLocations.length === 0) {
    return
  }
  const selectedPracticeId = provider.providerLocations[0].practiceId

  // Find if this location is already selected as additional
  const existingLocationIndex = provider.providerLocations.findIndex(
    (loc) => loc.locationId == locationId && loc.isPrimary === false,
  )

  if (existingLocationIndex >= 0) {
    // Remove the location
    provider.providerLocations.splice(existingLocationIndex, 1)
    console.log(`Removed additional location ${locationId} for provider ${providerIndex}`)
  } else {
    // Add the location as additional (not primary)
    const locationDetails = allLocations.value.find((loc) => loc.locationId == locationId)
    provider.providerLocations.push({
      practiceId: selectedPracticeId,
      locationId: locationId,
      // LocationName: locationDetails?.locationName || '',
      isPrimary: false,
    })
    console.log(`Added additional location ${locationId} for provider ${providerIndex}`)
  }
}

// Track which provider should be expanded (only the most recently added)
const lastAddedProviderId = ref(null)

// Force re-render key
const renderKey = ref(0)

// Function to determine if a provider should be expanded
const shouldExpandProvider = (provider, index) => {
  // If there's only one provider, it should always be expanded
  if (providers.value.length === 1) {
    return true
  }
  // If this provider was the last one added
  if (lastAddedProviderId.value == provider.providerId) {
    return true
  }
  // If this is the last provider in the array and no specific provider is marked
  if (index === providers.value.length - 1 && !lastAddedProviderId.value) {
    return true
  }
  return false
}

// Ensure all existing providers have IDs and proper structure
const ensureProviderIds = () => {
  providers.value.forEach((provider) => {
    if (!provider.providerId) {
      provider.providerId = formStore.generateId()
    }
    // Only add default providerLocations if the array doesn't exist or is completely empty
    // Don't reset existing selections
    if (!provider.providerLocations) {
      provider.providerLocations = [
        {
          practiceId: '',
          locationId: '',
          isPrimary: true,
        },
      ]
    } else if (provider.providerLocations.length === 0) {
      provider.providerLocations.push({
        practiceId: '',
        locationId: '',
        isPrimary: true,
      })
    }
  })
}

// Function to add a new provider
const addProvider = () => {
  const newProvider = {
    providerId: formStore.generateId(),
    firstName: '',
    middleInitial: '',
    lastName: '',
    suffix: '',
    alias: '',
    degree: '',
    npi: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    primarySpecialty: '',
    secondarySpecialty: '',
    cms: false,
    isPrivleged: false,
    providerLocations: [
      {
        practiceId: '',
        locationId: '',
        isPrimary: true,
      },
    ],
  }

  // Mark this as the last added provider (this will cause only it to be expanded)
  lastAddedProviderId.value = newProvider.providerId

  // Add to local ref (will sync to store via watcher)
  providers.value.push(newProvider)

  // Force re-render to update collapse states
  renderKey.value++

  console.log('Added new provider with ID:', newProvider.providerId)
}


// Function to remove a specific provider by index
const removeProviderByIndex = (index) => {
  if (providers.value.length > 1 && index >= 0 && index < providers.value.length) {
    const providerToRemove = providers.value[index]
    providers.value.splice(index, 1)
    console.log('Removed provider at index', index, ':', providerToRemove.providerId)

    // If only one provider remains, mark it as the one to expand
    if (providers.value.length === 1) {
      lastAddedProviderId.value = providers.value[0].providerId
      // Force re-render to update collapse states
      renderKey.value++
    }
  }
}

// Use step validation composable

// Direct step validation for Step 5
const isStep5Valid = computed(() => {
  if (providers.value.length === 0) {
    return false
  }

  return providers.value.every((provider) => {
    const hasName = provider.firstName && provider.lastName
    const hasDob = provider.dateOfBirth && provider.dateOfBirth.length === 10 // YYYY-MM-DD
    const hasNpi = provider.npi && provider.npi.length === 10
    const hasDegree = Boolean(provider.degree)
    const hasContact = provider.phone && provider.email
    const hasCms = Boolean(provider.cms)
    const hasSpecialty = Boolean(provider.primarySpecialty)
    const hasPrivileges = Boolean(provider.isPrivleged)

    // Location validation
    const hasPractice = provider.providerLocations?.[0]?.practiceId

    // For "add" applications, if a practice has no locations from TIN search,
    // we should not require a location to be selected
    let locationValidation = true
    if (hasPractice) {
      const availableLocationsForPractice = allLocations.value.filter(
        (location) => location.practiceId == hasPractice
      )

      // Only require location selection if locations are available for this practice
      if (availableLocationsForPractice.length > 0) {
        locationValidation = provider.providerLocations?.some(
          (loc) => loc.isPrimary && loc.locationId,
        )
      }
    } else {
      locationValidation = false // Practice is required
    }

    return (
      hasName &&
      hasDob &&
      hasNpi &&
      hasDegree &&
      hasContact &&
      hasCms &&
      hasSpecialty &&
      hasPrivileges &&
      hasPractice &&
      locationValidation
    )
  })
})

// Watch for validation changes and emit to parent
watch(
  isStep5Valid,
  (newValid) => {
    formStore.setStepValidation(5, newValid)
    emit('validity-changed', newValid)
  },
  { immediate: true },
)

// Watch for changes in practices/locations from the store and reinitialize
watch(
  () => formStore.membershipApplicationInfo.practices,
  () => {
    if (!isInitializing.value) {
      initializeLocations()
    }
  },
  { deep: true }
)

// Track if component is still initializing
const isInitializing = ref(true)

// Optimized watcher for practice selection changes
watch(
  () => providers.value.map((p) => p.providerLocations[0]?.practiceId),
  (newPracticeIds, oldPracticeIds) => {
    // Don't reset locations during initialization or if no old values exist
    if (isInitializing.value || !oldPracticeIds) {
      return
    }

    providers.value.forEach((provider, index) => {
      const newPracticeId = newPracticeIds[index]
      const oldPracticeId = oldPracticeIds[index]

      console.log(`Provider ${index} practice change: old=${oldPracticeId} (${typeof oldPracticeId}) -> new=${newPracticeId} (${typeof newPracticeId})`)

      if (newPracticeId !== oldPracticeId) {
        // Reset location structure when practice changes
        provider.providerLocations = [
          {
            practiceId: newPracticeId || '',
            locationId: '',
            isPrimary: true,
          },
        ]
        console.log(`Updated provider ${index} practice assignment to ${newPracticeId || 'none'}`)
        console.log('Available locations after practice change:', allLocations.value)
        console.log('Locations for this practice:', allLocations.value.filter(loc => loc.practiceId == newPracticeId))
      }
    })
  },
)

// Set up initial state on mount
onMounted(async () => {
  console.log('StepFiveProvidersComponent mounted')
  console.log('Application type:', formStore.applicationType)
  console.log('Practices in store:', formStore.membershipApplicationInfo.practices)

  // Initialize providers first
  initializeProviders()

  // Ensure provider IDs are set
  ensureProviderIds()

  // If no providers exist, create one
  if (providers.value.length === 0) {
    addProvider()
  }

  // Initialize locations after providers are ready
  initializeLocations()

  // Set the first provider as the initially expanded one if no other is marked
  if (providers.value.length > 0 && !lastAddedProviderId.value) {
    lastAddedProviderId.value = providers.value[0].providerId
  }

  // Use nextTick to ensure DOM is updated before marking initialization complete
  await nextTick()

  // Mark initialization as complete
  isInitializing.value = false
})
</script>

<style scoped>
hr {
  height: 1px;
}
</style>
